import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { dateToInX } from '@helpers/dateToInX';
import { PrismaOrganizationApiKeyDto } from 'app/API';
import { useState } from 'react';

type TProps = {
  keys: PrismaOrganizationApiKeyDto[];
  selectedKeyId: string;
  onSelect: (id: string) => void;
  onCreateKey: () => void;
  loading: boolean;
};

const describeKey = (key: PrismaOrganizationApiKeyDto): string => {
  const shortId = key.id.slice(0, 8);
  if (!key.expirationDate) {
    return `${shortId}… — never expires`;
  }
  const expired = new Date(key.expirationDate) <= new Date();
  return `${shortId}… — ${expired ? 'expired' : `expires ${dateToInX(key.expirationDate, false)}`}`;
};

export const ApiKeyPanel = ({ keys, selectedKeyId, onSelect, onCreateKey, loading }: TProps) => {
  const [copied, setCopied] = useState(false);

  const copyKey = async () => {
    if (!selectedKeyId) {
      return;
    }
    await navigator.clipboard.writeText(selectedKeyId);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="mb-8 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-4 py-4">
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="text-sm font-semibold text-[var(--color-title)]">API key</span>
        <OutlinedButton className="w-[140px]" disabled={loading} onClick={onCreateKey}>
          Create new key
        </OutlinedButton>
      </div>
      <p className="mb-3 text-xs text-[var(--color-text-subtle)]">
        Requests below are sent with the selected key in the <code className="font-mono">api-key</code> header. Keys are
        managed on the Organization details page.
      </p>

      {keys.length === 0 ? (
        <p className="text-sm text-[var(--color-text)]">
          {loading ? 'Loading keys…' : 'No API keys yet — create one to send requests.'}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <select
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)]"
            value={selectedKeyId}
            onChange={(event) => onSelect(event.target.value)}
          >
            {keys.map((key) => (
              <option key={key.id} value={key.id}>
                {describeKey(key)}
              </option>
            ))}
          </select>

          {selectedKeyId ? (
            <div className="flex items-center gap-2">
              <code className="flex-1 overflow-x-auto whitespace-nowrap rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 font-mono text-xs text-[var(--color-text)]">
                {selectedKeyId}
              </code>
              <OutlinedButton className="w-[90px]" disabled={false} onClick={copyKey}>
                {copied ? 'Copied' : 'Copy'}
              </OutlinedButton>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
